import { SeasonStatus } from '@sulis-liiga/common';
import type { Season } from '@sulis-liiga/common';

/**
 * The season whose divisions the enroll picker should enumerate. Order:
 * ACTIVE → earliest UPCOMING → most recent COMPLETED.
 *
 * Enrollment itself is now division-directed, not season-directed — the
 * backend decides whether an enrollment lands on the upcoming season's roster
 * or is `queued` (season-less) when there is no upcoming season, and no
 * `seasonId` is sent on the enroll request. This resolver is only used to pick
 * which season's divisions to fetch so the picker has ladders to show: an
 * ACTIVE season is closed to new enrollment but still has the league's ladder
 * structure, an UPCOMING season is the one enrollment actually targets, and a
 * COMPLETED season is the last resort — falling back to it lets a captain see
 * (and enroll into, ending up `queued`) the league's ladders even when the
 * admin hasn't created the next season yet.
 */
/**
 * The season a viewer should land on by default when browsing a league's
 * divisions/standings. Order: latest ACTIVE season, else the latest COMPLETED
 * season. UPCOMING seasons are intentionally skipped — they hold no results yet
 * and an admin can still select one manually from the season dropdown.
 *
 * "Latest" is by `startDate` descending (matching the backend's own
 * `findByLeague` ordering); there is normally only one ACTIVE season per league.
 */
export function resolveViewingSeason(seasons: Season[] | undefined | null): Season | undefined {
  if (!seasons || seasons.length === 0) return undefined;

  const latestActive = seasons
    .filter(s => s.status === SeasonStatus.ACTIVE)
    .slice()
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0];
  if (latestActive) return latestActive;

  return seasons
    .filter(s => s.status === SeasonStatus.COMPLETED)
    .slice()
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0];
}

export function resolveEnrollmentSeason(seasons: Season[] | undefined | null): Season | undefined {
  if (!seasons || seasons.length === 0) return undefined;
  const active = seasons.find(s => s.status === SeasonStatus.ACTIVE);
  if (active) return active;

  const earliestUpcoming = seasons
    .filter(s => s.status === SeasonStatus.UPCOMING)
    .slice()
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())[0];
  if (earliestUpcoming) return earliestUpcoming;

  return seasons
    .filter(s => s.status === SeasonStatus.COMPLETED)
    .slice()
    .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())[0];
}
